// ─── useContextOptions ───────────────────────────────────────────────────────
// Fetches persona, objective and tone options from the backend context API.
// Falls back to hardcoded options on error. Keyed on the UI language: the lists
// are localised server-side, so a language switch re-fetches all three.

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchPersonas, fetchObjectives, fetchTonePresets } from '../services/api/backendClient.js';
import { PERSONA_OPTIONS, OBJECTIVE_OPTIONS, TONE_OPTIONS, CUSTOM_TONE_OPTION } from '../constants/options.js';
import { registerTonePresets, resetTonePresets } from '../utils/toneMigration.js';

function humanize(str) {
  return str
    .replace(/-/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

function toOption(item) {
  const value = item.id || item.value;
  const label = item.label || item.name || humanize(value);
  return { value, label };
}

// Tone keeps is_brand_default so the reconcile effect can target the brand entry
// explicitly rather than trusting the documented "first entry" ordering.
// `source: 'backend'` marks the entries whose label must WIN over i18n — the
// hardcoded fallback and the FE-only `custom` entry stay i18n-first, otherwise
// an English locale would render 'Personalizzato*' / 'Brand Voice (da KB)'.
function toToneOption(item) {
  return { ...toOption(item), is_brand_default: item.is_brand_default ?? null, source: 'backend' };
}

export function useContextOptions() {
  const { i18n } = useTranslation();
  const language = i18n.language?.split('-')[0] === 'en' ? 'en' : 'it';

  const [personaOptions, setPersonaOptions] = useState(PERSONA_OPTIONS);
  const [objectiveOptions, setObjectiveOptions] = useState(OBJECTIVE_OPTIONS);
  const [toneOptions, setToneOptions] = useState(TONE_OPTIONS);

  const loadOptions = useCallback((isCancelled) => {
    fetchPersonas(language)
      .then(data => {
        if (isCancelled()) return;
        const opts = Array.isArray(data) ? data.map(toOption) : [];
        if (opts.length > 0) setPersonaOptions(opts);
      })
      .catch(() => { /* keep fallback */ });

    fetchObjectives(language)
      .then(data => {
        if (isCancelled()) return;
        const opts = Array.isArray(data) ? data.map(toOption) : [];
        if (opts.length > 0) setObjectiveOptions(opts);
      })
      .catch(() => { /* keep fallback */ });

    // `custom` is appended last: it is a frontend-only escape hatch the backend
    // neither serves nor accepts as a literal.
    fetchTonePresets(language)
      .then(data => {
        if (isCancelled()) return;
        const opts = Array.isArray(data) ? data.map(toToneOption) : [];
        if (opts.length > 0) {
          const withCustom = [...opts, CUSTOM_TONE_OPTION];
          setToneOptions(withCustom);
          // Publish to the module registry so the non-React inverters
          // (toneStateFromParams) recognise tenant-specific slugs.
          registerTonePresets(withCustom);
        }
      })
      .catch(() => { /* keep fallback */ });
  }, [language]);

  useEffect(() => {
    let cancelled = false;
    loadOptions(() => cancelled);

    // Clearing the registry on teardown keeps it from outliving the tenant it
    // belongs to. Presets are per-tenant and the registry is module-global, read
    // by non-React inverters (toneStateFromParams) anywhere in the app; a tenant
    // switch is a SPA navigate, not a reload, so without this the previous
    // tenant's slugs would stay "known". The cost is that a remount re-inverts
    // with an empty registry until the fetch lands — which the orchestrator's
    // tone reconcile effect self-heals.
    return () => { cancelled = true; resetTonePresets(); };
  }, [loadOptions]);

  return { personaOptions, objectiveOptions, toneOptions };
}

// Standalone tone-preset fetch for surfaces that need the served labels but not
// the persona/objective lists — currently the member / tenant-admin run detail.
// Returns [] before the fetch lands and on failure, so callers degrade to the
// i18n label rather than showing a bare slug.
//
// Pass enabled=false for superadmins: presets are per-tenant, and an SA viewing
// another tenant's run would get their OWN tenant's labels, which is worse than
// the i18n fallback.
//
// Deliberately does NOT append CUSTOM_TONE_OPTION (this is a read-only display
// list; `custom` is never a stored tone_hint) and does NOT call
// registerTonePresets — a read-only surface should not mutate global state.
export function useTonePresets(enabled = true) {
  const { i18n } = useTranslation();
  const language = i18n.language?.split('-')[0] === 'en' ? 'en' : 'it';

  const [presets, setPresets] = useState([]);

  useEffect(() => {
    if (!enabled) { setPresets([]); return undefined; }
    let cancelled = false;

    fetchTonePresets(language)
      .then(data => {
        if (cancelled) return;
        const opts = Array.isArray(data) ? data.map(toToneOption) : [];
        if (opts.length > 0) setPresets(opts);
      })
      .catch(() => { /* keep [] → i18n fallback */ });

    return () => { cancelled = true; };
  }, [language, enabled]);

  return presets;
}
